import { ArrowLeft, Minus, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { ClubType, CustomExercise } from '../../lib/planning.ts';
import {
    BALL_CHOICES, ROUND_CHOICES, archiveExercise, boundLabel, createExercise,
    listClubTypes, listExercises, updateExercise,
} from '../../lib/planning.ts';

/* Composer un exercice.
 *
 * Tous sont bâtis sur le mode libre : l'application n'impose ni rythme ni
 * posture, le joueur pose des bornes. C'est la seule façon d'ouvrir la
 * composition sans qu'une idée d'exercice devienne une migration.
 */

/* Une borne : un nombre qu'on ajuste, et des raccourcis pour y aller vite.
 *
 * ── UN seul endroit dit la valeur ────────────────────────────────
 *
 * Les raccourcis se sont d'abord allumés quand la valeur tombait sur l'un
 * d'eux. Deux affichages pour un même nombre, dont l'un ne sait pas représenter
 * toutes les valeurs : à 2, plus rien n'était allumé dans la rangée, et monter
 * de 1 en 1 depuis « Libre » se lisait « 1, puis 3 » pour qui regardait les
 * pastilles. Le pas à pas incrémentait pourtant bien de un — c'est la rangée
 * qui sautait.
 *
 * Les raccourcis ne portent donc plus d'état : ce sont des ACTIONS, pas un
 * choix. Le nombre est le seul à dire où l'on en est, et rien ne peut plus se
 * contredire.
 *
 * C'est le partage déjà retenu sur la saisie de distance : les panneaux du
 * practice y sont des actions — un appui enregistre — et le sélecteur porte la
 * valeur. Même raison des deux côtés : le clavier du téléphone est l'ennemi de
 * ces écrans, il recouvre la moitié de l'interface et demande de viser des
 * touches de six millimètres avec un gant.
 *
 * ── Pourquoi « − » s'arrête à 1 et ne rend pas la main à « Libre » ────────
 *
 * « Libre » n'est pas zéro, c'est l'absence de borne. Le faire surgir sous 1
 * changerait la nature du réglage par un appui destiné à le diminuer, alors
 * que le raccourci qui le dit est juste en dessous.
 */
function BoundChoice({ label, hint, unite, choices, value, onPick }: {
    label: string;
    hint: string;
    /** Au singulier, ARTICLE COMPRIS : « une balle », « une série ». Le genre
     *  ne se devine pas depuis le nom seul, et « Un balle de moins » lu à voix
     *  haute par un lecteur d'écran est une faute qu'on entend. */
    unite: string;
    choices: (number | null)[];
    value: number | null;
    onPick: (v: number | null) => void;
}) {
    // La borne haute est celle de l'API et de la base : au-delà, l'écran
    // proposerait une valeur que l'enregistrement refuserait.
    const MAXI = 50;
    const cran = (delta: number): void => {
        onPick(value === null ? 1 : Math.min(MAXI, Math.max(1, value + delta)));
    };
    const rond = 'btn btn-ghost size-14 shrink-0 rounded-full border border-base-300'
        + ' bg-base-100 hover:bg-base-300 disabled:opacity-40';

    return (
        <div>
            <span className="mb-1 block text-sm font-medium">{label}</span>

            {/* La valeur, EN PREMIER et en grand : c'est l'état du réglage, et
                le seul endroit où il s'écrit. Les raccourcis viennent après
                parce qu'ils ne font que l'atteindre plus vite. */}
            <div className="flex items-center justify-center gap-5 py-1">
                <button type="button" onClick={() => cran(-1)}
                    disabled={value !== null && value <= 1}
                    aria-label={`${unite} de moins`} className={rond}>
                    <Minus size={22} />
                </button>
                <span aria-live="polite"
                    className="min-w-20 text-center text-3xl font-bold tabular-nums">
                    {value === null
                        ? <span className="text-lg font-semibold opacity-60">Libre</span>
                        : value}
                </span>
                <button type="button" onClick={() => cran(1)}
                    disabled={value !== null && value >= MAXI}
                    aria-label={`${unite} de plus`} className={rond}>
                    <Plus size={22} />
                </button>
            </div>

            {/* Des raccourcis, sans état allumé : ils POSENT la valeur, ils ne
                la montrent pas. C'est le nombre au-dessus qui la montre. */}
            <div className="mt-1 grid grid-cols-5 gap-2">
                {choices.map((c) => (
                    <button
                        key={String(c)}
                        type="button"
                        onClick={() => onPick(c)}
                        aria-label={c === null
                            ? 'Sans borne'
                            : `${c} ${unite.replace(/^une? /, '')}${c > 1 ? 's' : ''}`}
                        className="h-12 rounded-field bg-base-100 text-sm font-semibold
                                   transition-colors hover:bg-base-300"
                    >
                        {boundLabel(c)}
                    </button>
                ))}
            </div>

            <span className="mt-1.5 block text-xs opacity-60">{hint}</span>
        </div>
    );
}

export function ExerciseEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const editing = id !== undefined && id !== 'nouveau';

    const [types, setTypes] = useState<ClubType[]>([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [balls, setBalls] = useState<number | null>(null);
    const [rounds, setRounds] = useState<number | null>(null);
    const [clubIds, setClubIds] = useState<Set<number>>(new Set());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        void listClubTypes().then(setTypes).catch(() => setError('Chargement impossible'));
        if (!editing) return;
        void listExercises().then((list) => {
            const e: CustomExercise | undefined = list.find((x) => x.id === id);
            if (!e) { setError('Exercice introuvable'); return; }
            setName(e.name);
            setDescription(e.description ?? '');
            setBalls(e.ballsPerRound);
            setRounds(e.rounds);
            setClubIds(new Set(e.clubTemplateIds));
        }).catch(() => setError('Chargement impossible'));
    }, [id, editing]);

    const toggleClub = (tid: number): void => {
        setClubIds((s) => {
            const next = new Set(s);
            if (next.has(tid)) next.delete(tid); else next.add(tid);
            return next;
        });
    };

    const save = async (): Promise<void> => {
        if (!name.trim()) { setError('Donnez un nom à cet exercice'); return; }
        setBusy(true); setError(null);
        const body = {
            name: name.trim(),
            description: description.trim() || null,
            ballsPerRound: balls,
            rounds,
            clubTemplateIds: [...clubIds],
        };
        try {
            if (editing) await updateExercise(id, body);
            else await createExercise(body);
            void navigate('/entrainement/exercices');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
            setBusy(false);
        }
    };

    const remove = async (): Promise<void> => {
        if (!editing) return;
        setBusy(true);
        try {
            await archiveExercise(id);
            void navigate('/entrainement/exercices');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/entrainement/exercices" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Exercices
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">
                    {editing ? "Modifier l'exercice" : 'Nouvel exercice'}
                </h1>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            <section className="space-y-3 rounded-box bg-base-200 p-4">
                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Nom</span>
                    <input
                        type="text" maxLength={40}
                        className="input input-bordered w-full"
                        placeholder="Pause au backswing"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    {/* Court, parce qu'il se lit sur un bouton, à bout de bras,
                        entre deux balles. */}
                    <span className="mt-1 block text-xs opacity-60">
                        Court : c'est ce que vous verrez sur le bouton.
                    </span>
                </label>

                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Consigne</span>
                    <textarea
                        rows={3} maxLength={1000}
                        className="textarea textarea-bordered w-full"
                        placeholder="Marquer un temps d'arrêt en haut du backswing, puis dérouler sans forcer."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <span className="mt-1 block text-xs opacity-60">
                        Affichée pendant l'exercice : c'est ce qu'on oublie en premier
                        après trois balles.
                    </span>
                </label>
            </section>

            <section className="space-y-4 rounded-box bg-base-200 p-4">
                {/* « Série », et non « tour ».
                    L'écran de saisie appelle SERIE le paquet de balles jouées
                    avec un club avant d'en changer, et c'est le mot que portent
                    aussi la synthèse, les statistiques et l'historique. Ce
                    compositeur était le seul à dire « tour » : deux mots pour
                    le même objet, sur des écrans voisins, se lisent comme deux
                    objets — et donc comme une inversion.

                    « Tour » est de surcroît déjà pris : c'est un parcours joué,
                    celui qu'on supprime dans « Supprimer ce tour ». */}
                <BoundChoice
                    label="Balles par série" unite="une balle"
                    choices={BALL_CHOICES} value={balls} onPick={setBalls}
                    hint="La série se ferme d'elle-même à la dernière balle, et l'on passe à la suivante. Libre : vous la clôturez quand vous le sentez."
                />
                <BoundChoice
                    label="Nombre de séries" unite="une série"
                    choices={ROUND_CHOICES} value={rounds} onPick={setRounds}
                    hint="Libre : l'exercice dure le temps que vous voulez."
                />
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-medium">Clubs autorisés</h2>
                {/* Des TYPES, pas les clubs du sac.
                    On travaille « au fer 9 », quel que soit le fer 9 qu'on a ce
                    jour-là — et l'exercice doit rester valable après un
                    changement de matériel. Les types absents du sac sont grisés
                    mais restent choisissables : on prépare parfois un exercice
                    pour un club qu'on s'apprête à acheter. */}
                <p className="mt-1 text-xs opacity-60">
                    {clubIds.size === 0
                        ? 'Aucun choix : club libre. C\'est le cas le plus courant.'
                        : `${clubIds.size} type${clubIds.size > 1 ? 's' : ''} retenu${clubIds.size > 1 ? 's' : ''}.`}
                    {' '}Les clubs que vous ne possédez pas sont grisés.
                </p>

                {clubIds.size > 0 && (
                    <button type="button" onClick={() => setClubIds(new Set())}
                        className="btn btn-ghost btn-xs mt-2">
                        Laisser le club libre
                    </button>
                )}

                <div className="mt-3 grid grid-cols-3 gap-2">
                    {types.map((t) => {
                        const on = clubIds.has(t.id);
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => toggleClub(t.id)}
                                aria-pressed={on}
                                /* Le grisé des types non possédés est calibré,
                                   pas choisi à l'œil : à 0,45 le libellé tombait
                                   à 2,84:1 — sous l'AA de 4,5:1 alors que le
                                   bouton reste CLIQUABLE. Un balayage avec un
                                   voile de soleil de 8 % donne 0,75 comme plus
                                   faible valeur qui tienne l'AA (4,95:1 sous
                                   voile).

                                   L'écart avec un type possédé (16,7:1) reste
                                   franc, et la graisse le redouble : un signal
                                   qui ne dépend ni de la couleur ni de la
                                   luminosité ambiante. */
                                className={`rounded-field px-2 py-2 text-xs transition-colors ${
                                    on
                                        ? 'bg-primary font-semibold text-primary-content'
                                        : `bg-base-100 hover:bg-base-300 ${t.owned ? 'font-semibold' : 'font-normal opacity-75'}`
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            <button type="button" disabled={busy} onClick={() => void save()}
                className="btn btn-primary btn-block h-14 text-lg font-bold tap-target">
                {editing ? 'Enregistrer' : "Créer l'exercice"}
            </button>

            {editing && (
                <div className="pt-2">
                    <button type="button" className="btn btn-ghost btn-sm gap-2 text-error"
                        onClick={() => setConfirmDelete(true)}>
                        <Trash2 size={16} /> Supprimer cet exercice
                    </button>
                </div>
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Supprimer « {name} » ?</h2>
                        {/* Dire ce qui NE disparaît pas : l'historique doit
                            rester lisible, sinon on hésite à faire le ménage. */}
                        <p className="mt-2 text-xs opacity-70">
                            Les entraînements déjà faits avec cet exercice restent dans votre
                            historique. Il disparaît seulement des listes et des séances.
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy} onClick={() => void remove()}
                                className="btn btn-error btn-block tap-target">Supprimer</button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmDelete(false)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
